import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import Dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import React from 'react'

import {
  fetchNotifications,
  fetchProgramMaterial,
  markNotificationsRead,
  saveOrderFeedback
} from 'api/procurement'
import constants from 'app/constants'
import { eventAlert, notify } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  title: { marginBottom: theme.spacing(2) },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#1565c0',
    marginRight: 8,
    display: 'inline-block'
  }
}))

// Notification link format: "<tab>:<centreId>:<YYYY-MM-01>" where <tab> is the
// Center-Team tab to open (program-material | consumables | petty-cash).
const NAV_TABS = {
  'program-material': 'program-material',
  consumables: 'consumables',
  'petty-cash': 'petty-cash'
}
const parseLink = (link) => {
  if (!link) return null
  const [prefix, centre, month] = link.split(':')
  if (!NAV_TABS[prefix] || !centre || !month) return null
  return { tab: NAV_TABS[prefix], category: prefix, centre, month }
}

const DECISION_CHIP = {
  accepted: { label: 'Accepted', style: { background: '#e8f5e9', color: '#2e7d32' } },
  rejected: { label: 'Rejected', style: { background: '#ffebee', color: '#c62828' } }
}

const Notifications = ({ onNavigate, centreId } = {}) => {
  const classes = useStyles()
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [po, setPo] = React.useState(null) // opened PO { centreLabel, month, ...res }
  const [poTarget, setPoTarget] = React.useState(null) // {centre, month} for saving
  const [feedback, setFeedback] = React.useState('')
  const [savingFeedback, setSavingFeedback] = React.useState(false)

  React.useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        // In the Center-Team view this is pinned to the selected centre, so the
        // tab shows only that centre's notifications (not every centre's).
        const res = await fetchNotifications(centreId)
        setItems(res?.results || [])
        // mark read once viewed (same centre scope)
        await markNotificationsRead(centreId)
      } catch (e) {
        /* ignore */
      } finally {
        setLoading(false)
      }
    })()
  }, [centreId])

  const openPo = async (target) => {
    try {
      const res = await fetchProgramMaterial({
        centre: target.centre,
        month: target.month
      })
      setPo({ month: target.month, ...res })
      setPoTarget(target)
      setFeedback(res?.centre_feedback || '')
    } catch (e) {
      /* ignore */
    }
  }

  // Centre can leave a remark once HQ has acted on the order.
  const canRemark =
    po && ['approved', 'sent', 'received', 'fulfilled'].includes(po.status)

  const submitFeedback = async () => {
    if (!poTarget) return
    setSavingFeedback(true)
    try {
      await saveOrderFeedback(poTarget.centre, poTarget.month, feedback.trim())
      notify('Remark sent to HQ', '', 'success')
      setPo(null)
      setPoTarget(null)
    } catch (error) {
      eventAlert(
        error?.message || 'Could not save remark',
        constants.ERROR,
        'Notifications'
      )
    } finally {
      setSavingFeedback(false)
    }
  }

  return (
    <Paper className={classes.root} elevation={0}>
      {/* No page heading — the tab already labels this "Notifications". */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : isEmpty(items) ? (
        <Typography variant="body2" color="textSecondary">
          No notifications. Approve or reject an order in the Approvals tab to
          generate one.
        </Typography>
      ) : (
        <List>
          {items.map((n) => {
            const target = parseLink(n.link)
            const isPm = target && target.category === 'program-material'
            return (
              <ListItem key={n.id} divider>
                <ListItemText
                  primary={
                    <span>
                      {!n.read && <span className={classes.unreadDot} />}
                      {n.title}
                    </span>
                  }
                  secondary={`${n.body} · ${Dayjs(n.created).format(
                    'DD MMM YYYY, HH:mm'
                  )}`}
                />
                {target ? (
                  <>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() =>
                        onNavigate
                          ? onNavigate(target.tab, target.month)
                          : isPm
                          ? openPo(target)
                          : null
                      }>
                      Open
                    </Button>
                    {isPm ? (
                      <Button size="small" onClick={() => openPo(target)}>
                        Remark
                      </Button>
                    ) : null}
                  </>
                ) : null}
              </ListItem>
            )
          })}
        </List>
      )}

      <Dialog open={!!po} onClose={() => setPo(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {po?.po_number || 'Program Material'} —{' '}
          {po ? Dayjs(po.month).format('MMMM YYYY') : ''}{' '}
          <span style={{ textTransform: 'lowercase', color: '#888' }}>
            ({po?.status})
          </span>
        </DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Unit Cost (₹)</TableCell>
                <TableCell align="right">Line Total (₹)</TableCell>
                <TableCell>Decision</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(po?.lines || []).map((l, i) => (
                <TableRow key={l.uuid || i}>
                  <TableCell>{l.item_name}</TableCell>
                  <TableCell align="right">{l.qty}</TableCell>
                  <TableCell align="right">{l.unit_cost}</TableCell>
                  <TableCell align="right">
                    {Number(
                      l.line_total != null
                        ? l.line_total
                        : Number(l.qty) * Number(l.unit_cost)
                    ).toLocaleString('en-IN')}
                  </TableCell>
                  <TableCell>
                    {DECISION_CHIP[l.line_decision] ? (
                      <Chip
                        size="small"
                        label={DECISION_CHIP[l.line_decision].label}
                        style={DECISION_CHIP[l.line_decision].style}
                      />
                    ) : (
                      <span style={{ color: '#999' }}>Pending</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {isEmpty(po?.lines) && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="textSecondary">
                      No lines.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {canRemark ? (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Your remark to HQ
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                placeholder="Add a remark on this order (e.g. received short, thanks, query)…"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPo(null)}>Close</Button>
          {canRemark ? (
            <Button
              variant="contained"
              color="primary"
              disabled={savingFeedback}
              onClick={submitFeedback}>
              {savingFeedback ? 'Saving…' : 'Save remark'}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default Notifications
